/**
 * Cluster key derivation per GraphNode for a chosen ClusterBy attribute.
 *
 * Returns null when the attribute doesn't apply to this node (e.g. clustering
 * by submissionType but the move is a sweep). Renderers should treat null as
 * "uncategorized" — typically rendered with a neutral tint, no cluster pull.
 *
 * Used by:
 *  - Nodes.tsx for cluster-tinting sphere color and bridge-ring color.
 *  - optimize.ts for same-cluster attractive force in the pre-sim pass.
 */
import type {
  ClusterBy,
  GraphNode,
  Move,
  MoveCategory,
  Position,
  Zone,
} from '../types/domain';

// Auto-derived disposition: a move's intent classification. No data
// edits required — keys off MoveCategory. Submission/sweep/takedown/
// pass are "moving the conflict forward in my favor" (offensive);
// escape/guard-retention are "neutralizing a threat" (defensive);
// transition is positional setup (transitional / neither).
function dispositionForCategory(c: MoveCategory): string {
  if (c === 'escape' || c === 'guard-retention') return 'defensive';
  if (c === 'transition') return 'transitional';
  return 'offensive';
}

function positionTypeForZone(zone: Zone): string | null {
  // Submitted = end-of-line, true terminal sink. Scrambled gets its
  // own 'flux' cluster — chaotic intermediate state that flows back
  // into any zone but visually deserves its own grouping. Folding it
  // into neutral made it disappear into the standing/clinch cluster
  // even though it's structurally different.
  if (zone === 'submitted') return 'terminal';
  if (zone === 'scrambled') return 'flux';
  if (zone === 'standing' || zone === 'clinch') return 'neutral';
  if (zone.endsWith('-top')) return 'top';
  if (zone.endsWith('-bottom')) return 'bottom';
  return null;
}

// Position-mode override: a "symmetric" position (50/50, double-pull
// open-guard, body-lock standoff) lives in guard-bottom geographically
// but has no top/bottom dominance — it should cluster with the truly
// neutral nodes (standing, clinch, etc.) instead of getting yanked into
// the bottom group. Driven by an explicit `symmetric` tag on the
// Position so it stays opt-in per-row.
function positionTypeForPosition(p: Position): string | null {
  if (p.tags?.includes('symmetric')) return 'neutral';
  return positionTypeForZone(p.zone);
}

/**
 * Families with at least two members.
 *
 * A `family` is a variant LINEAGE, and the family lens pulls every
 * member toward a shared centroid and paints them one colour. A family
 * of one therefore renders as its own cluster with its own colour and
 * its own centroid — 35 such families produced 35 meaningless clusters
 * and diluted the palette for the real ones.
 *
 * Rather than deleting the field from the data (a `twister` genuinely IS
 * a twister-lineage technique; it just has no sibling YET, and will the
 * moment a second variant is added) the LENS ignores lineages it can't
 * meaningfully draw. The data stays truthful, the view stays legible,
 * and a family becomes visible automatically as soon as it has company.
 *
 * Memoised on the identity of the two maps, which are themselves
 * memoised upstream — so this recomputes only on a genuine data swap,
 * not per node or per frame.
 */
let familyCacheKey: unknown = null;
let familyCache: Set<string> = new Set();
function multiMemberFamilies(
  positionsById: Map<string, Position>,
  movesById: Map<string, Move>,
): Set<string> {
  const key = `${positionsById.size}:${movesById.size}`;
  if (familyCacheKey === key) return familyCache;
  const counts = new Map<string, number>();
  for (const p of positionsById.values())
    if (p.family) counts.set(p.family, (counts.get(p.family) ?? 0) + 1);
  for (const m of movesById.values())
    if (m.family) counts.set(m.family, (counts.get(m.family) ?? 0) + 1);
  familyCache = new Set(
    [...counts.entries()].filter(([, n]) => n >= 2).map(([f]) => f),
  );
  familyCacheKey = key;
  return familyCache;
}

/**
 * Who is winning — a separate question from where the body is.
 *
 * These two were the same field until the back family forced them apart.
 * The player who has the back is winning decisively and, in the reclined
 * back control this data describes, is also underneath. Asking the zone
 * suffix to express both meant one of those had to be wrong.
 *
 * So `positionType` clusters by geography and this clusters by advantage,
 * and a node is free to be bottom-and-winning. That combination is not an
 * inconsistency to be cleaned up — it is the Truck, the Crucifix, the
 * rear triangle, and back control itself.
 *
 * Moves use `performedBy`, which is who does the move rather than where
 * it happens, and is derived separately for exactly this reason.
 */
function advantageFor(
  node: GraphNode,
  positionsById: Map<string, Position>,
  movesById: Map<string, Move>,
): string | null {
  if (node.kind === 'position') {
    const p = positionsById.get(node.id) as (Position & { dominance?: string }) | undefined;
    return p?.dominance ?? null;
  }
  if (node.kind === 'move') {
    const m = movesById.get(node.id) as (Move & { performedBy?: string }) | undefined;
    return m?.performedBy ?? null;
  }
  return null;
}

export function clusterIdFor(
  node: GraphNode,
  clusterBy: ClusterBy,
  positionsById: Map<string, Position>,
  movesById: Map<string, Move>,
): string | null {
  if (clusterBy === 'none') return null;
  if (node.kind === 'interception') return null;

  if (clusterBy === 'positionType') {
    if (node.kind === 'position') {
      const p = positionsById.get(node.id);
      return p ? positionTypeForPosition(p) : null;
    }
    // In move-mode the node is a move; cluster by its from-position type.
    if (node.kind === 'move') {
      const m = movesById.get(node.id);
      if (!m) return null;
      const p = positionsById.get(m.from);
      return p ? positionTypeForPosition(p) : null;
    }
    return null;
  }

  if (clusterBy === 'advantage') {
    return advantageFor(node, positionsById, movesById);
  }

  if (clusterBy === 'posture') {
    // Posture is intrinsic to the position's body shape. In move-mode
    // we mirror the same fall-through positionType uses: a move is
    // grouped by its from-position's posture.
    if (node.kind === 'position') {
      const p = positionsById.get(node.id);
      return p?.posture ?? null;
    }
    if (node.kind === 'move') {
      const m = movesById.get(node.id);
      if (!m) return null;
      const p = positionsById.get(m.from);
      return p?.posture ?? null;
    }
    return null;
  }

  if (clusterBy === 'moveCategory') {
    if (node.kind === 'move') {
      return movesById.get(node.id)?.category ?? null;
    }
    // In position-mode, position nodes don't have a single category.
    // They could be clustered by the dominant outgoing category, but
    // that's lossy; for now we return null and let them be uncategorized.
    return null;
  }

  if (clusterBy === 'submissionType') {
    if (node.kind === 'move') {
      const m = movesById.get(node.id);
      if (m?.category !== 'submission') return null;
      return m.submissionType ?? 'other';
    }
    // For position nodes: submission-type clustering doesn't really apply.
    return null;
  }

  if (clusterBy === 'disposition') {
    if (node.kind === 'move') {
      const m = movesById.get(node.id);
      if (!m) return null;
      return dispositionForCategory(m.category);
    }
    // Position nodes: no single disposition. Leave uncategorized so the
    // axis stays meaningful in move-mode and harmless if accidentally
    // set in position-mode.
    return null;
  }

  if (clusterBy === 'family') {
    // Family clusters every variant of a position/move toward the same
    // centroid — saddleentry / 411entry / honeyholeentry all gather
    // around the saddle family. Strong intra-family attraction; the
    // collapse-variants HUD toggle becomes redundant when this is
    // active, but they cooperate (collapsed view + family-cluster =
    // tight canonical-only constellation).
    // Singleton lineages are treated as uncategorised — see
    // multiMemberFamilies() for why this is a lens rule and not a data
    // edit.
    const real = multiMemberFamilies(positionsById, movesById);
    if (node.kind === 'position') {
      const f = positionsById.get(node.id)?.family;
      return f && real.has(f) ? f : null;
    }
    if (node.kind === 'move') {
      const f = movesById.get(node.id)?.family;
      return f && real.has(f) ? f : null;
    }
    return null;
  }

  if (clusterBy === 'concept') {
    // Concept clusters by the FIRST listed concept tag. Multi-concept
    // entities are arbitrarily assigned to their first concept for
    // single-cluster lookups (force-sim cluster pull, node-color
    // tinting). For surfaces that want full multi-membership (cluster
    // key listing, cluster-aware visibility filter), use
    // clusterIdsFor() instead.
    const concepts =
      node.kind === 'position'
        ? positionsById.get(node.id)?.concepts
        : node.kind === 'move'
          ? movesById.get(node.id)?.concepts
          : undefined;
    return concepts && concepts.length > 0 ? concepts[0] : null;
  }

  return null;
}

/**
 * Multi-membership version of clusterIdFor. Returns every cluster a
 * node belongs to, not just the primary. Today only `concept` is
 * meaningfully multi-valued; every other axis is single-valued and we
 * delegate to clusterIdFor and wrap the result.
 *
 * Use this for the cluster key listing (so a move with multiple
 * concepts surfaces under each one) and the cluster-aware visibility
 * filter (so a node passes if ANY of its cluster ids is non-null).
 *
 * Do NOT use this for force-sim membership / centroid pull — those
 * paths still want a single primary cluster to avoid double-pulling a
 * node toward multiple centroids per tick.
 */
export function clusterIdsFor(
  node: GraphNode,
  clusterBy: ClusterBy,
  positionsById: Map<string, Position>,
  movesById: Map<string, Move>,
): string[] {
  if (clusterBy === 'concept') {
    const concepts =
      node.kind === 'position'
        ? positionsById.get(node.id)?.concepts
        : node.kind === 'move'
          ? movesById.get(node.id)?.concepts
          : undefined;
    return concepts && concepts.length > 0 ? [...concepts] : [];
  }
  // Single-valued axes: just wrap the primary id.
  const primary = clusterIdFor(node, clusterBy, positionsById, movesById);
  return primary ? [primary] : [];
}

/**
 * A second, finer grouping applied INSIDE the primary cluster.
 *
 * The primary axis answers one question — where the body is, or who is
 * winning — and gathers a third of the map into each answer. That is a
 * useful shape and a coarse one: "bottom" is 90-odd nodes with no
 * internal order, so the eye has nowhere to go after the first split.
 *
 * Family is the natural second level, because the lineage work already
 * built it. Every node knows its variant family, and those families are
 * exactly the groups a reader would draw by hand: the saddle entries
 * together, the knee shields together, the rides together.
 *
 * SCOPED TO THE PRIMARY, DELIBERATELY.
 * The key is `primary::family`, not `family`. Two knee-shield nodes on
 * opposite sides of a top/bottom split are in the same family but should
 * NOT drag toward each other — doing so would pull nodes across the very
 * boundary the primary axis exists to draw, and the two forces would
 * fight. Composing the key means a family knot forms once per primary
 * region it appears in, which is the tree shape rather than a tug of war.
 *
 * Singleton families are excluded for the same reason the family LENS
 * excludes them: a sub-cluster of one is a node with extra force applied
 * and no group to join.
 */
export function subClusterIdFor(
  node: GraphNode,
  clusterBy: ClusterBy,
  positionsById: Map<string, Position>,
  movesById: Map<string, Move>,
): string | null {
  if (clusterBy === 'none' || clusterBy === 'family') return null;
  const primary = clusterIdFor(node, clusterBy, positionsById, movesById);
  if (!primary) return null;

  const real = multiMemberFamilies(positionsById, movesById);
  const family =
    node.kind === 'position'
      ? positionsById.get(node.id)?.family
      : node.kind === 'move'
        ? movesById.get(node.id)?.family
        : undefined;
  if (!family || !real.has(family)) return null;
  return `${primary}::${family}`;
}
